<template>
  <div>
    <DataTable
      v-model:filters="filters"
      v-model:expandedRows="expandedRows"
      :value="items"
      :loading="loading"
      :rows="10"
      lazy
      row-hover
      paginator
      :totalRecords="total"
      :rowsPerPageOptions="[10, 25, 50]"
      class="p-datatable-sm"
      dataKey="id"
      filterDisplay="row"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      currentPageReportTemplate="Memuat {first} dari {last} data dari total {totalRecords}"
      scrollable
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
    >
      <template #empty>
        <div class="text-center">Tidak ada data.</div>
      </template>
      <template #loading>
        <div class="text-center">Memuat data, harap tunggu...</div>
      </template>
      <Column
        field="pajak_no"
        header="No. Pajak"
        style="min-width: 170px"
        sortable
      >
        <template #body="{ data }">
          {{ data.pajak_no }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            type="text"
            v-model="filterModel.value"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
        <template #footer> Sisa Open : </template>
      </Column>
      <Column
        sortable
        dataType="date"
        filter-field="created_between"
        field="created_at"
        header="Tgl Dibuat"
        style="min-width: 190px"
      >
        <template #body="{ data }">
          {{ formatDate(data.created_at) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <CalendarFilter
            ref="calendarFilter"
            v-model="filterModel.value"
            class="p-column-filter"
            @select="filterCallback()"
          />
        </template>
        <template #footer>
          <InputText
            v-model="totalOpen"
            type="text"
            class="p-column-filter text-right"
            style="max-width: 100px"
            disabled
          />
        </template>
      </Column>
      <Column
        sortable
        dataType="date"
        filter-field="expired_between"
        field="expired_at"
        header="Expire"
        style="min-width: 190px"
      >
        <template #body="{ data }">
          {{ formatDate(data.expired_at) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <CalendarFilter
            v-model="filterModel.value"
            class="p-column-filter"
            @select="filterCallback()"
          />
        </template>
      </Column>
      <Column field="si_no" header="No. Invoice" sortable>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            type="text"
            v-model="filterModel.value"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
        <template #body="{ data }">
          {{ data.si_no != null ? data.si_no : '-' }}
        </template>
      </Column>
      <Column field="si_at" header="Tgl. Invoice" filter-field="order_between" sortable>
        <template #body="{ data }">
          {{ formatDate(data.si_at) }}
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <CalendarFilter
            v-model="filterModel.value"
            class="p-column-filter"
            @select="filterCallback()"
          />
        </template>
      </Column>
      <Column sortable field="status" header="Status">
        <template #body="{ data }">
          <div
            :class="
              'status-badge status-' +
              (data.status === 'open'
                ? 'valid'
                : data.status === 'assigned'
                ? 'pick'
                : 'draft')
            "
          >
            {{ data.status }}
          </div>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <Dropdown
            v-model="filterModel.value"
            :options="[
              { id: 'open', nama: 'Open' },
              { id: 'assigned', nama: 'Assigned' },
              { id: 'rejected', nama: 'Rejected' },
            ]"
            :showClear="true"
            optionValue="id"
            optionLabel="nama"
            class="p-column-filter text-right"
            @change="filterCallback()"
          />
        </template>
      </Column>
      <Column
        style="
          display: flex;
          flex-grow: 1;
          justify-content: flex-end;
          min-width: 200px;
        "
      >
        <template #body="{ data }">
          <Button
            type="button"
            icon="pi pi-pencil"
            class="p-button-outlined p-button-secondary p-button-text"
            @click="$emit('edit', data)"
          />
          <Button
            :disabled="data.status !== 'open'"
            type="button"
            icon="pi pi-trash"
            class="p-button-outlined p-button-danger p-button-text"
            @click="$emit('delete', data)"
          />
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { FilterMatchMode } from 'primevue/api'
import { formatCurrency, formatDate } from '@/helpers'
import CalendarFilter from '@/components/CalendarFilter'

export default {
  props: {
    items: {
      type: Array,
      default() {
        return []
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    total: {
      type: Number,
      default: 0,
    },
    open: {
      type: Number,
      default: 0,
    },
    gridOptions: {
      type: Object,
      default() {
        return {}
      },
    },
  },
  components: {
    CalendarFilter,
  },
  data() {
    return {
      expandedRows: [],
      options: this.gridOptions,
      filters: {
        pajak_no: { value: null, matchMode: FilterMatchMode.CONTAINS },
        si_no: { value: null, matchMode: FilterMatchMode.CONTAINS },
        created_between: { value: null, matchMode: FilterMatchMode.CONTAINS },
        expired_between: { value: null, matchMode: FilterMatchMode.CONTAINS },
        process_between: { value: null, matchMode: FilterMatchMode.CONTAINS },
        status: { value: null, matchMode: FilterMatchMode.CONTAINS },
        order_between: { value: null, matchMode: FilterMatchMode.CONTAINS },
      },
    }
  },
  watch: {
    'options.filters'(value) {
      if (!value.created_between.value) {
        this.$refs.calendarFilter.clearValueOnly()
      }
    },
  },
  computed: {
    totalOpen() {
      return this.open
    },
  },
  methods: {
    onPage(e) {
      e.page = e.page + 1
      e.sortField = this.options.sortField
      e.sortOrder = this.options.sortOrder
      this.options = e
      this.options.filters = this.filters
      this.loadData()
    },
    onSort(e) {
      this.options = e
      this.loadData()
    },
    onFilter() {
      this.options.page = 1
      this.options.rows = 10
      this.options.sortField = 'pajak_no'
      this.options.sortOrder = -1
      this.options.filters = this.filters
      this.loadData()
    },
    loadData() {
      this.$emit('request', this.options)
    },
    formatCurrency(value, minDigit = 0, maxDigit = 0) {
      return formatCurrency(value, minDigit, maxDigit)
    },
    formatDate(value) {
      return formatDate(value)
    },
  },
}
</script>
